import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Languages, Volume2, Pause, Send, ArrowUpFromLine, X, Mic, Square, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import axios from "axios";
import moment from "moment";
import caresathiLogo from "../../Assets/Images/caresathi/caresathi_logo_ai.svg";

// ── Gradient helpers ───────────────────────────────────────────────────────
const gradientText = {
  backgroundImage: "var(--primary-gradient)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
};

const SUGGESTIONS = {
  en: ["What are diabetes symptoms?", "How to manage High BP?", "Exercises for back pain?"],
  hi: ["मधुमेह के लक्षण क्या हैं?", "उच्च रक्तचाप को कैसे प्रबंधित करें?", "पीठ दर्द के लिए व्यायाम?"],
  or: ["ମଧୁମେହର ଲକ୍ଷଣ କ'ଣ?", "ଉଚ୍ଚ ରକ୍ତଚାପ କିପରି ନିୟନ୍ତ୍ରଣ କରିବେ?", "ପିଠ ଯନ୍ତ୍ରଣା ପାଇଁ ବ୍ୟାୟାମ?"],
  bn: ["ডায়াবেটিসের লক্ষণ কী?", "উচ্চ রক্তচাপ কীভাবে নিয়ন্ত্রণ করবেন?", "পিঠের ব্যথার জন্য ব্যায়াম?"],
  ta: ["நீரிழிவு அறிகுறிகள் என்ன?", "உயர் இரத்த அழுத்தத்தை எவ்வாறு நிர்வகிப்பது?", "முதுகு வலிக்கான உடற்பயிற்சிகள்?"],
  te: ["మధుమేహ లక్షణాలు ఏమిటి?", "అధిక రక్తపోటును ఎలా నిర్వహించాలి?", "వెన్నునొప్పికి వ్యాయామాలు?"],
  kn: ["ಮಧುಮೇಹದ ಲಕ್ಷಣಗಳು ಯಾವುವು?", "ಅಧಿಕ ರಕ್ತದೊತ್ತಡವನ್ನು ಹೇಗೆ ನಿರ್ವಹಿಸುವುದು?", "ಬೆನ್ನುನೋವಿಗೆ ವ್ಯಾಯಾಮಗಳು?"],
  mr: ["मधुमेहाची लक्षणे काय आहेत?", "उच्च रक्तदाब कसा व्यवस्थापित करावा?", "पाठदुखीसाठी व्यायाम?"], 
};

// ── Helpers ────────────────────────────────────────────────────────────────
const genrateRandomTimestamp = () => {
  const t = moment().format("YYYYMMDDHHmmss");
  const r = Math.floor(Math.random() * 1000);
  return `${t}${r}`;
};

const groupSessionsByDate = (sessions,activeSessionId) => {
  const groups = {};
  const sorted = [...sessions].sort((a, b) => {
    if (String(a.session_id) === String(activeSessionId)) return -1;
    if (String(b.session_id) === String(activeSessionId)) return 1;
    return new Date(b.updated_at) - new Date(a.updated_at);
  });
  sorted.forEach((s) => {
    const d = s.updated_at ? new Date(s.updated_at) : new Date();
    const today = new Date();
    // Compare calendar days (not raw ms) so that yesterday never appears as today
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const sessionStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.floor((todayStart - sessionStart) / 86400000);
    let label =
      diffDays === 0 ? "Today"
      : diffDays === 1 ? "Yesterday"
      : diffDays < 7 ? `${diffDays} days back`
      : diffDays < 30 ? "This month"
      : "Older";
    groups[label] = groups[label] || [];
    groups[label].push(s);
  });
  return groups;
};

const getDcsBase = () =>
  (process.env.REACT_APP_DCS || "https://digitalconciergeservice.curebay.in/").replace(/\/+$/, "");

// ── Component ──────────────────────────────────────────────────────────────
const Newcaresathi = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toastRef = useRef(null);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const selectedLanguageRef = useRef("");

  // ── Auth / user ──────────────────────────────────────────────────────────
  const userData = (() => { try { return JSON.parse(localStorage.getItem("loginObj")); } catch { return null; } })() || {};
  const s_token = localStorage.getItem("token");
  const userName =
    userData?.patientName ||
    (userData?.firstName ? `${userData.firstName}${userData.lastName ? " " + userData.lastName : ""}` : null) ||
    userData?.name || "there";

  // ── Query params (lab report deep-link) ─────────────────────────────────
  const queryParams = new URLSearchParams(location.search);
  const lab_report_analyser_popup = queryParams.get("popup");
  const reportUrl = queryParams.get("reportUrl");
  const reportExtType = queryParams.get("reportExtType");

  // ── Core state ───────────────────────────────────────────────────────────
  const [qaHistory, setQaHistory] = useState([]);
  const [sampleMessage, setSampleMessage] = useState("");
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  const [newchartflag, setNewchartflag] = useState(false);
  const [chartEnded, setchartEnded] = useState(false);
  const [textAnimationFinish, setTextAnimationFinish] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [currentServiceType, setCurrentServiceType] = useState("base");
  const [useDigitalPrescription, setUseDigitalPrescription] = useState(false);

  // ── Session / history ────────────────────────────────────────────────────
  const [sessionId, setSessionId] = useState(() => genrateRandomTimestamp());
  const [chatHistory, setChatHistory] = useState([]);
  const [timeStamp, setTimestamp] = useState("");
  const [currentChatId, setCurrentChatId] = useState(null);
  const sessionIdRef = useRef(sessionId);

  // ── File upload ──────────────────────────────────────────────────────────
  const [base64pdf, setBase64pdf] = useState("");
  const [documanttype, setDocumanttype] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [uploadedFileDisplayName, setUploadedFileDisplayName] = useState("");
  const [currentAttachment, setCurrentAttachment] = useState(null);
  const [openLabReportModal, setOpenLabReportModal] = useState(false);

  // ── Snackbar ─────────────────────────────────────────────────────────────
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarmsg, setSnackbarmsg] = useState({ msg: "", type: "" });

  // ── Audio / TTS ──────────────────────────────────────────────────────────
  const [audioInstances, setAudioInstances] = useState({});
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const audioUrlsRef = useRef({});
  const [loadingAudioId, setLoadingAudioId] = useState(null);

  // ── Mic / Transcribe ─────────────────────────────────────────────────────
  // micState: 'idle' | 'recording' | 'transcribing'
  const [micState, setMicState] = useState("idle");
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingMimeRef = useRef("");

  // ── Translation ──────────────────────────────────────────────────────────
  const [convertedText, setConvertedText] = useState({ index: -1, type: "", text: "" });
  const [supportedLanguages, setSupportedLanguages] = useState({});
  const translateCacheRef = useRef({});
  const translatedIndexRef = useRef(null); // index of the message just translated, so the scroll effect stays on it

  // ── Rating ───────────────────────────────────────────────────────────────
  const [showRatingModal, setShowRatingModal] = useState(false);

  // ── Mobile sidebar toggle ─────────────────────────────────────────────────
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // ── WebSocket refs ───────────────────────────────────────────────────────
  const wsRef = useRef(null);
  const wsIdRef = useRef(0);
  const wsMessageHandlerRef = useRef(null);
  const streamingTextRef = useRef("");
  const lastQuestionRef = useRef("");
  const pendingMessageRef = useRef(null);
  const pendingToolNameRef = useRef(null);
  const pendingToolArgsRef = useRef(null);

  // Add state near other file upload states
const [uploadingFile, setUploadingFile] = useState(false);
// language select — persisted so navigating away (e.g. to lab report upload) and back doesn't re-show the modal
const [selectedLanguage, setSelectedLanguage] = useState(() => sessionStorage.getItem('caresathi_lang') || "");
const [langModalOpen, setLangModalOpen] = useState(() => !sessionStorage.getItem('caresathi_lang'));
const namingInProgressRef = useRef(false);
const triedNamingRef = useRef(new Set());
const historyLoadingRef = useRef(false);


// ── Language Modal Component ──────────────────────────────────────────────
const LanguageSelectionModal = () => {
  const [hovered, setHovered] = useState(null);
  const langs = Object.keys(supportedLanguages).length > 0
    ? supportedLanguages
    : { en: "English", hi: "Hindi", or: "Odia", bn: "Bengali", ta: "Tamil", te: "Telugu", kn: "Kannada", mr: "Marathi" };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: "var(--section-bg-gradient), #fff" }}>
      <div className="bg-white rounded-[24px] shadow-[0px_0px_15px_0px_#80DAF1] p-8 w-full max-w-[420px]">
        <div className="text-4xl text-center mb-2">🌐</div>
        <h2 className="text-[22px] font-bold text-center mb-1" style={gradientText}>Choose Your Language</h2>
        <p className="text-[13px] text-gray-400 text-center mb-6">Select your preferred language to get started</p>
        <div className="flex flex-col gap-3 max-h-[60vh] overflow-y-auto pr-1">
          {Object.entries(langs).map(([code, name]) => (
            <button
              key={code}
             onClick={() => {
  sessionStorage.setItem('caresathi_lang', code);
  setSelectedLanguage(code);
  selectedLanguageRef.current = code;   // ← update ref immediately
  setLangModalOpen(false);
  connectWS(sessionIdRef.current, userData.code || "");  // reconnect with language
}}
              onMouseEnter={() => setHovered(code)}
              onMouseLeave={() => setHovered(null)}
              className="flex items-center gap-4 px-5 py-3.5 rounded-[14px] border transition-all text-left w-full"
              style={{
                background: hovered === code ? "var(--primary-gradient-180)" : "#fff",
                color: hovered === code ? "#fff" : "var(--color-brand-primary)",
                borderColor: hovered === code ? "transparent" : "var(--color-brand-primary)",
                transform: hovered === code ? "scale(1.02)" : "scale(1)",
              }}
            >
              <span className="text-[13px] font-semibold uppercase w-7 flex-shrink-0"
                style={{ color: hovered === code ? "#fff" : "var(--color-brand-primary)" }}>
                {code.toUpperCase()}
              </span>
              <span className="flex-1 text-[15px] font-semibold">{name}</span>
            </button>
          ))}
        </div>
        <button

  onClick={() => {
    sessionStorage.setItem('caresathi_lang', 'en');
    selectedLanguageRef.current = "en";
    setSelectedLanguage("en");
    setLangModalOpen(false);
  }}
          className="mt-4 w-full py-2.5 text-[13px] text-gray-400 hover:text-gray-600 transition-colors"
        >
          Skip — use default
        </button>
      </div>
    </div>
  );
};

  // ── Helpers ──────────────────────────────────────────────────────────────
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scroll({ top: chatContainerRef.current.scrollHeight, behavior: "smooth" });
    }
  };

  const showToast = (severity, msg) => toastRef.current?.show({ severity, summary: msg, life: 3000 });

  const detectMajorLanguage = (text) => {
    const total = text.length;
    if (!total) return "en";
    const en = Array.from(text).filter((c) => c.charCodeAt(0) <= 0x007f).length;
    const hi = Array.from(text).filter((c) => c.charCodeAt(0) >= 0x0900 && c.charCodeAt(0) <= 0x097f).length;
    const or = Array.from(text).filter((c) => c.charCodeAt(0) >= 0x0b00 && c.charCodeAt(0) <= 0x0b7f).length;
    return [["en", en], ["hi", hi], ["or", or]].sort((a, b) => b[1] - a[1])[0][0];
  };

  const stripHtmlTags = (html) => {
    const d = document.createElement("div");
    d.innerHTML = html;
    return (d.textContent || d.innerText || "").replace(/\*/g, "");
  };

  const getFileNameFromUrl = (url) => {
    try {
      const name = new URL(url).pathname.split("/").pop() || "File";
      return name.length > 30 ? `Report.${name.split(".").pop()}` : name;
    } catch { return "File"; }
  };

  // ── WebSocket ─────────────────────────────────────────────────────────────
  const connectWS = (sessionIdStr, patientId) => {
    const wsId = ++wsIdRef.current;
    wsRef.current?.close(1000, "new session");
    wsRef.current = null; 
    setIsConnected(false);

    const wsUrl = getDcsBase().replace(/^https/, "wss").replace(/^http/, "ws") + "/ws/chat";
    let ws;
    try { ws = new WebSocket(wsUrl); wsRef.current = ws; } catch (e) { return; }

    // ws.onopen = () => {
    //   if (wsIdRef.current !== wsId) { ws.close(1000, "stale"); return; }
    //   setIsConnected(true);
    //   ws.send(JSON.stringify({ type: "init", session_id: sessionIdStr, patient_id: patientId || "unknown", prompt: "auto", tool_tags: ["utility"], source_channel: "B2C", tts_enabled: false }));
    //   getChatHistory();
    // };

    ws.onopen = () => {
  if (wsIdRef.current !== wsId) { ws.close(1000, "stale"); return; }
  setIsConnected(true);

  const initPayload = {
    type: "init",
    session_id: sessionIdStr,
    patient_id: patientId || "unknown",
    prompt: "auto",
    tool_tags: ["utility"],
    source_channel: "B2C",
    tts_enabled: false,
    ...(selectedLanguageRef.current && {       // ← use ref, not state
      language: selectedLanguageRef.current,
      language_confirmed: true,
    }),
  };

  ws.send(JSON.stringify(initPayload));
  getChatHistory();
};

  
    ws.onmessage = (event) => {
      if (wsIdRef.current !== wsId) return;
      try { const msg = JSON.parse(event.data); if (msg.type === "init_ack" && pendingMessageRef.current) { ws.send(JSON.stringify(pendingMessageRef.current)); pendingMessageRef.current = null; } wsMessageHandlerRef.current?.(msg); } catch (e) {}
    };
    ws.onerror = () => { if (wsIdRef.current !== wsId) return; setIsConnected(false); };
    ws.onclose = () => { if (wsRef.current === ws) { wsRef.current = null; setIsConnected(false); } };
  };

  const [isConnected, setIsConnected] = useState(false);

  // ── Data fetching ─────────────────────────────────────────────────────────
  const getChatHistory = () => {
  if (!userData.code) return;
  fetch(`${getDcsBase()}/api/sessions?patient_id=${encodeURIComponent(userData.code)}`)
    .then(async (res) => {
      if (!res.ok) return;
      const sessions = await res.json();
      if (!Array.isArray(sessions) || !sessions.length) return;

      setChatHistory(sessions);

      // Only batch name sessions we haven't tried yet
      const needsName = sessions
        .filter((s) => {
          const isCurrentEmpty =
            String(s.session_id) === String(sessionIdRef.current) &&
            qaHistory.length === 0;
          return (
            !s.session_name &&
            /^\d+$/.test(s.session_id) &&
            !isCurrentEmpty &&
            !triedNamingRef.current.has(String(s.session_id)) // ✅ skip already tried
          );
        })
        .map((s) => s.session_id);

      if (needsName.length > 0) {
        // ✅ Mark all as tried BEFORE calling so repeat getChatHistory calls don't re-fire
        needsName.forEach((id) => triedNamingRef.current.add(id));
        generateSessionNamesBatch(needsName);
      }
    })
    .catch(() => {});
};

 const generateSessionNamesBatch = async (sessionIds) => {
  if (namingInProgressRef.current) return; // ✅ skip if already running
  namingInProgressRef.current = true;
  try {
    const resp = await fetch(`${getDcsBase()}/api/sessions/names`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_ids: sessionIds }),
    });
    if (!resp.ok) return;
    const data = await resp.json();

    const nameMap = {};
    (data.sessions || []).forEach((s) => {
      if (s.session_name && s.source !== "pending") {
        nameMap[String(s.session_id)] = s.session_name;
      }
    });

    if (Object.keys(nameMap).length === 0) return;

    setChatHistory((prev) =>
      prev.map((s) =>
        nameMap[String(s.session_id)]
          ? { ...s, session_name: nameMap[String(s.session_id)] }
          : s
      )
    );
  } catch {}
  finally {
    namingInProgressRef.current = false; // ✅ release lock
  }
};
  const fetchLabReportAsBase64 = async (url, type) => {
    try {
      setUploadingFile(true);
      const blob = await (await fetch(url)).blob();
      
      // Set base64 as before
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Content = reader.result.split(",")[1];
        setDocumanttype(type);
        setBase64pdf(base64Content);
      };
      reader.readAsDataURL(blob);

      // Also upload to get attachment_id — same as handleFileChange
      const fileName = getFileNameFromUrl(url);
      const file = new File([blob], fileName, { type });
      const formData = new FormData();
      formData.append("file", file, fileName);
      formData.append("patient_id", userData.code || "unknown");
      formData.append("session_id", sessionId.toString());

      const res = await fetch(`${getDcsBase()}/api/upload`, { method: "POST", body: formData });
      if (!res.ok) throw new Error();
      const uploadData = await res.json();

      setCurrentAttachment(uploadData);
      setUploadedFileName(url);
      setUploadedFileDisplayName(fileName);
      setOpenLabReportModal(false);
    } catch {
      showToast("error", "Failed to load lab report.");
    } finally {
      setUploadingFile(false);
    }
  };

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!qaHistory.length) return;
    // A translation only rewrites an existing answer — keep the view on that answer instead of jumping to the bottom
    if (translatedIndexRef.current !== null) {
      const idx = translatedIndexRef.current;
      translatedIndexRef.current = null;
      const container = chatContainerRef.current;
      const bubble = container?.querySelector(`[data-answer-index="${idx}"]`);
      if (container && bubble) {
        const top = bubble.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 12;
        container.scroll({ top, behavior: "smooth" });
      }
      return;
    }
    scrollToBottom();
  }, [qaHistory]);
  useEffect(() => { return () => { wsIdRef.current++; wsRef.current?.close(1000, "unmount"); Object.values(audioUrlsRef.current).forEach(URL.revokeObjectURL); }; }, []);
  useEffect(() => { if (reportUrl && reportExtType) fetchLabReportAsBase64(reportUrl, reportExtType); }, [reportUrl]);
  useEffect(() => { 
  if (lab_report_analyser_popup === "true" && !reportUrl) setOpenLabReportModal(true); 
}, [lab_report_analyser_popup]);
  useEffect(() => { if (textAnimationFinish) { /* service recommendations already set per-message */ } }, [textAnimationFinish]);
  useEffect(() => { if (!loading) scrollToBottom(); }, [loading]);
  useEffect(() => { if (chartEnded) setShowRatingModal(true); }, [chartEnded]);
  useEffect(() => { if (userData.code) getChatHistory(); }, [userData.code]);
  useEffect(() => {
    fetch(`${getDcsBase()}/api/languages`).then((r) => (r.ok ? r.json() : null)).then((d) => { if (d?.languages) setSupportedLanguages(d.languages); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (sessionId) { sessionIdRef.current = sessionId.toString(); connectWS(sessionId.toString(), userData.code || ""); }
    return () => { wsIdRef.current++; };
  }, [sessionId]);
  useEffect(() => {
    if (userData.code) {
      const token = process.env.REACT_APP_KEY;
      axios.get(`${process.env.REACT_APP_NEWBASEURL}PatientSubscription/list?patientId=${userData.code}`, { headers: { "x-api-key": token } })
        .then((res) => { const valid = (res?.data?.data || []).filter((s) => s.status !== 5); setHasData(valid.length > 0); })
        .catch(() => setHasData(false));
    }
  }, [userData.code]);
  //lang change
  useEffect(() => {
  selectedLanguageRef.current = selectedLanguage;
}, [selectedLanguage]);

  // ── WebSocket message handler ─────────────────────────────────────────────
  wsMessageHandlerRef.current = (data) => {
    if (data.type === "text") {
      streamingTextRef.current += data.content || "";
      setQaHistory((prev) => prev.map((item, idx) => idx === prev.length - 1 ? { ...item, answer: streamingTextRef.current, isLoading: false } : item));
    } else if (data.type === "done") {
      const rawText = streamingTextRef.current;
      const lang = data.language || "en";
      const msgId = data.message_id || null;
      setQaHistory((prev) => prev.map((item, idx) => idx === prev.length - 1 ? { ...item, answer: rawText, isLoading: false, answerLanguage: lang, messageId: msgId } : item));
      setLoading(false);
      setTextAnimationFinish(true);
      saveMessageToApi({ session_id: sessionIdRef.current, patient_id: userData.code, question: lastQuestionRef.current, answer: rawText });
      streamingTextRef.current = "";
      scrollToBottom();
    } else if (data.type === "tool_call") {
      pendingToolNameRef.current = data.name;
      pendingToolArgsRef.current = data.args || {};
    } else if (data.type === "tool_result") {
      try {
        const result = JSON.parse(data.result || "{}");
        const toolName = pendingToolNameRef.current;
        const toolArgs = pendingToolArgsRef.current || {};
        const link = result.redirectUrl || result.booking_link || result.link || result.url || "";
        const serviceName = toolArgs.service_name || toolArgs.solution_name || toolArgs.service || toolName || "";
        const serviceType = toolArgs.service || toolArgs.consult_type || toolArgs.solution_name || toolName || "";
        if (serviceName) setQaHistory((prev) => prev.map((item, idx) => idx === prev.length - 1 ? { ...item, serviceRecommendation: [...(item.serviceRecommendation || []), { service_name: serviceName, link, service_type: serviceType }] } : item));
      } catch {} finally { pendingToolNameRef.current = null; pendingToolArgsRef.current = null; }
      } else if (data.type === "ai_solution") {
  const { solution_key, display_name, url } = data;
  if (solution_key && url) {
    setQaHistory((prev) =>
      prev.map((item, idx) =>
        idx === prev.length - 1
          ? { ...item, aiSolution: { solution_key, display_name, url } }
          : item
      )
    );
  }
    } else if (data.type === "error") {
      setLoading(false);
      setQaHistory((prev) => prev.map((item, idx) => idx === prev.length - 1 ? { ...item, isLoading: false } : item));
      showToast("error", data.content || "Something went wrong.");
    }
    
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  const saveMessageToApi = async (payload) => {
    try {
      await fetch(`${process.env.REACT_APP_NEWBASEURL}ChatBotQa/`, { 
        method: "POST", 
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${s_token}` }, 
        body: JSON.stringify(payload) 
      });

      // ── Name the session from first message if not named yet ──
      // ✅ Use bulk endpoint, only on first message
      const isFirstMessage = qaHistory.length <= 1;
      if (isFirstMessage && payload.session_id) {
        try {
          await fetch(`${getDcsBase()}/api/sessions/names`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              session_ids: [payload.session_id],
            }),
          });
        } catch {}
      }
      // ─────────────────────────────────────────────────────────

      
    } catch {}
  };

  const sendMessage = async (serviceType = "base", userPrompt = sampleMessage || input) => {
    setShowHeader(false);
    if (loading) { showToast("warn", "Please wait for the previous response."); return; }
    if (!userPrompt?.trim()) { showToast("warn", "Please write a message."); return; }

    const text = userPrompt.trim();
    setQaHistory((prev) => [...prev, { question: text, answer: "", isLoading: true, questionLanguage: detectMajorLanguage(text.slice(0, 40)), serviceRecommendation: null }]);
    setLoading(true);
    // Optimistically add/update session name from first message
if (qaHistory.length === 0) {
  const tempName = text.slice(0, 40);
  setChatHistory((prev) => {
    const exists = prev.some(
      (s) => String(s.session_id) === String(sessionIdRef.current)
    );
    if (exists) {
      return prev.map((s) =>
        String(s.session_id) === String(sessionIdRef.current)
          ? { ...s, session_name: tempName, updated_at: new Date().toISOString() }
          : s
      );
    }
    return [
      { session_id: sessionIdRef.current, session_name: tempName, updated_at: new Date().toISOString() },
      ...prev,
    ];
  });
}
    streamingTextRef.current = "";
    lastQuestionRef.current = text;
    setTextAnimationFinish(false);

    const wsMsg = { type: "message", content: text };
    if (currentAttachment) wsMsg.attachment_id = currentAttachment.attachment_id;

    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify(wsMsg));
    else { pendingMessageRef.current = wsMsg; connectWS(sessionIdRef.current, userData.code || ""); }

    setSampleMessage(""); setInput("");
    setUploadedFileName(""); setUploadedFileDisplayName(""); setCurrentAttachment(null);
    scrollToBottom();
  };

  const handleNewChatWindow = () => {
    // Stop any audio that is currently playing before clearing state
    Object.values(audioInstances).forEach((a) => { try { a.pause(); a.currentTime = 0; } catch {} });
    setIsConnected(false); setLoading(false); pendingMessageRef.current = null;
    const newId = genrateRandomTimestamp();
    setSessionId(newId); setTimestamp(""); setShowHeader(true); setchartEnded(false);
    setQaHistory([]); setNewchartflag(true); setCurrentChatId(null);
    setConvertedText({ index: -1, type: "", text: "" });
    setCurrentServiceType("base"); setCurrentAttachment(null);
    setUploadedFileName(""); setUploadedFileDisplayName(""); setBase64pdf("");
    setPlayingAudioId(null); getChatHistory();
  };

  const handleChatHistoryClick = async (clickedSessionId) => {
    if (historyLoadingRef.current) return;
    historyLoadingRef.current = true;
    Object.values(audioInstances).forEach((a) => { try { a.pause(); a.currentTime = 0; } catch {} });
    setPlayingAudioId(null); setShowHeader(false); setLoading(false);
    setConvertedText({ index: -1, type: "", text: "" });
    try {
      const resp = await fetch(`${getDcsBase()}/api/history/${encodeURIComponent(clickedSessionId)}`);
      if (!resp.ok) throw new Error();
      const data = await resp.json();
      const result = (data.messages || [])
        .map((msg) => {
          let serviceRecommendation = null;
          if (Array.isArray(msg.tool_calls) && msg.tool_calls.length > 0) {
            const recs = msg.tool_calls.flatMap((tc) => { try { const r = JSON.parse(tc.result || "{}"); const n = tc.args?.service_name || tc.args?.solution_name || tc.name || ""; const l = r.redirectUrl || r.booking_link || r.link || ""; return n ? [{ service_name: n, link: l, service_type: tc.args?.service || tc.name || "" }] : []; } catch { return []; } });
            if (recs.length) serviceRecommendation = recs;
          }
          return { question: msg.user_message, answer: msg.llm_response || "", isLoading: false, created_date: msg.created_at, serviceRecommendation, messageId: msg.id || null, answerLanguage: msg.language || "en" };
        })
        .sort((a, b) => new Date(a.created_date) - new Date(b.created_date));
      sessionIdRef.current = String(clickedSessionId);
      setTimestamp(String(clickedSessionId)); setQaHistory(result);
      // Don't scroll or connect WS immediately — just show the history.
      // WS will connect automatically when the user sends a message.
    } catch { showToast("error", "Failed to load chat history."); }
    finally { historyLoadingRef.current = false; }
  };

  // ── ADD THIS FUNCTION after handleChatHistoryClick ────────────────
const handleDeleteSession = async (sid) => {
  try {
    const res = await fetch(`${getDcsBase()}/api/sessions/${encodeURIComponent(sid)}`, {
      method: "DELETE",
    });
    if (!res.ok) throw new Error();
    
    // Remove from local state immediately
    setChatHistory((prev) => prev.filter((s) => String(s.session_id) !== String(sid)));
    
    // If deleted session was active, start a new chat
    if (String(sessionIdRef.current) === String(sid)) {
      handleNewChatWindow();
    }
    
    showToast("success", "Session deleted");
  } catch {
    showToast("error", "Failed to delete session");
  }
};
// ─────────────────────────────────────────────────────────────────

  const handleFileChange = async (event) => {
    if (!event.target.files.length) return;
    const file = event.target.files[0];
    setOpenLabReportModal(false);
    setUploadingFile(true);
    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("patient_id", userData.code || "unknown");
    formData.append("session_id", sessionId.toString());
    try {
      const res = await fetch(`${getDcsBase()}/api/upload`, { method: "POST", body: formData });
      if (!res.ok) throw new Error();
      const uploadData = await res.json();
      setCurrentAttachment(uploadData); setUploadedFileName(file.name); setUploadedFileDisplayName(file.name);
    } catch { showToast("error", "File upload failed."); }
      finally {
    setUploadingFile(false);
    }
  };

  const handleSpeak = async (answer, index) => {
    const plainText = stripHtmlTags(answer);
    const stopAudio = (idx) => { audioInstances[idx]?.pause(); if (audioUrlsRef.current[idx]) { URL.revokeObjectURL(audioUrlsRef.current[idx]); delete audioUrlsRef.current[idx]; } };
    if (playingAudioId === index) { stopAudio(index); setPlayingAudioId(null); return; }
    if (playingAudioId !== null) { stopAudio(playingAudioId); setPlayingAudioId(null); }
    const lang = qaHistory[index]?.answerLanguage || detectMajorLanguage(plainText);
    setLoadingAudioId(index);
    try {
      const resp = await fetch(`${getDcsBase()}/api/tts`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ lang, text: plainText, format: "mp3" }) });

      if (!resp.ok) throw new Error();
      const url = URL.createObjectURL(await resp.blob());
      audioUrlsRef.current[index] = url;
      const audio = new Audio(url);
      setAudioInstances((prev) => ({ ...prev, [index]: audio }));
      setPlayingAudioId(index);
      audio.play();
      audio.onended = () => { URL.revokeObjectURL(url); delete audioUrlsRef.current[index]; setPlayingAudioId(null); };
      audio.onerror = () => { URL.revokeObjectURL(url); delete audioUrlsRef.current[index]; setPlayingAudioId(null); };
    } catch { showToast("error", "Failed to play audio."); }
    finally {
    setLoadingAudioId(null);        // ← hide spinner
  }
  };

  const getLangItems = (arrayReference, type, text, language) => {
    const langs = Object.keys(supportedLanguages).length > 0 ? supportedLanguages : { en: "English", hi: "Hindi", bn: "Bengali", or: "Odia", ta: "Tamil", te: "Telugu", kn: "Kannada", mr: "Marathi" };
    return Object.entries(langs).filter(([code]) => code !== language).map(([code, name]) => ({ code, name, onClick: () => onClickLanguageDropdown(arrayReference, type, text, language, code) }));
  };

  const onClickLanguageDropdown = async (arrayRef, type, text, src, tgt) => {
    const msgId = qaHistory[arrayRef]?.messageId;
    const field = type === "answer" ? "llm_response" : "user_message";
    const key = `${msgId}:${field}:${tgt}`;
    if (translateCacheRef.current[key]) {
      const cached = translateCacheRef.current[key];
      translatedIndexRef.current = arrayRef;
      setConvertedText({ index: arrayRef, type, text: cached });
      setQaHistory((prev) => { const u = [...prev]; u[arrayRef] = { ...u[arrayRef], ...(type === "answer" ? { answerLanguage: tgt, answer: cached } : { questionLanguage: tgt, question: cached }) }; return u; });
      return;
    }
    if (!msgId) { showToast("warn", "Translation only available for saved messages."); return; }
    try {
      const resp = await fetch(`${getDcsBase()}/api/translate`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message_id: msgId, target_lang: tgt, field }) });
      if (!resp.ok) throw new Error();
      const data = await resp.json();
      const translated = data.translated_text || "";
      translateCacheRef.current[key] = translated;
      translatedIndexRef.current = arrayRef;
      setConvertedText({ index: arrayRef, type, text: translated });
      setQaHistory((prev) => { const u = [...prev]; u[arrayRef] = { ...u[arrayRef], ...(type === "answer" ? { answerLanguage: tgt, answer: translated } : { questionLanguage: tgt, question: translated }) }; return u; });
    } catch { showToast("error", "Translation failed."); }
  };

  // ── Mic / Transcribe handlers ─────────────────────────────────────────────
  const getSupportedRecordingMime = () => {
    const types = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/mp4",
    ];
    for (const t of types) {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(t)) return t;
    }
    return "";
  };

  const transcribeAudio = async (blob, mimeType) => {
    setMicState("transcribing");
    const ext = mimeType === "audio/mp4" ? "mp4" : mimeType === "audio/ogg" ? "ogg" : "webm";
    const formData = new FormData();
    formData.append("file", blob, `recording.${ext}`);

    try {
      const resp = await fetch(`${getDcsBase()}/api/transcribe`, { method: "POST", body: formData });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      if (data.transcript) {
        setInput((prev) => {
          const existing = (prev || "").trim();
          const next = existing ? existing + " " + data.transcript : data.transcript;
          setSampleMessage(next);
          return next;
        });
      } else {
        showToast("warn", "Transcription returned empty text.");
      }
    } catch (e) {
      showToast("error", `Transcription failed: ${e.message}`);
    } finally {
      setMicState("idle");
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      showToast("error", "Microphone not supported in this browser.");
      return;
    }
    const mime = getSupportedRecordingMime();
    if (!mime) {
      showToast("error", "No supported audio recording format found.");
      return;
    }
    recordingMimeRef.current = mime;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: mime });
      mediaRecorderRef.current = mr;
      recordedChunksRef.current = [];

      mr.ondataavailable = (e) => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const baseMime = recordingMimeRef.current.split(";")[0];
        const blob = new Blob(recordedChunksRef.current, { type: baseMime });
        recordedChunksRef.current = [];
        if (blob.size === 0) {
          showToast("error", "Recording was empty.");
          setMicState("idle");
          return;
        }
        await transcribeAudio(blob, baseMime);
      };

      mr.start();
      setMicState("recording");
    } catch (e) {
      showToast("error", `Microphone access denied: ${e.message}`);
      setMicState("idle");
    }
  };

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      mr.stop();
      setMicState("transcribing");
    }
  };

  const toggleRecording = () => {
    if (micState === "recording") stopRecording();
    else if (micState === "idle") startRecording();
  };

  // Stop any active recording on unmount
  useEffect(() => {
    return () => {
      const mr = mediaRecorderRef.current;
      if (mr && mr.state !== "inactive") {
        try { mr.stream.getTracks().forEach((t) => t.stop()); } catch {}
        try { mr.stop(); } catch {}
      }
    };
  }, []);

  const handleKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage("base", sampleMessage || input); } };

  // ── Render ─────────────────────────────────────────────────────────────────
  
// Inject current session at top if it has messages but isn't in history yet
const currentSessionInHistory = chatHistory.some(
  (s) => String(s.session_id) === String(sessionIdRef.current)
);

const historyToShow = chatHistory.filter((s) => {
  // Hide sessions with no real name
  if (!s.session_name || s.session_name === "Unnamed session") return false;
  // Hide current session if no messages yet
  if (
    String(s.session_id) === String(sessionIdRef.current) &&
    qaHistory.length === 0
  ) return false;
  return true;
});

if (qaHistory.length > 0 && !currentSessionInHistory) {
  historyToShow.unshift({
    session_id: sessionIdRef.current,
    session_name: qaHistory[0]?.question?.slice(0, 40) || "Current chat",
    updated_at: new Date().toISOString(),
  });
}

const groupedHistory = groupSessionsByDate(historyToShow, sessionIdRef.current);
  // Shared sidebar JSX
  const sidebarInner = (
    <>
      {/* Header */}
      <div className="bg-white px-6 py-[22px] flex items-center justify-between shadow-[0px_5px_6px_0px_rgba(0,0,0,0.03)]">
        <div className="flex items-center gap-3">
          <svg width="14" height="11" viewBox="0 0 14 11" fill="none">
            <rect width="14" height="1.8" rx="0.9" fill="url(#mg)" />
            <rect y="4.6" width="14" height="1.8" rx="0.9" fill="url(#mg)" />
            <rect y="9.2" width="14" height="1.8" rx="0.9" fill="url(#mg)" />
            <defs><linearGradient id="mg" x1="0" y1="0" x2="14" y2="0" gradientUnits="userSpaceOnUse"><stop stopColor="#004276" /><stop offset="1" stopColor="#004276" /></linearGradient></defs>
          </svg>
          <span className="text-[20px] font-medium" style={gradientText}>Chat History</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleNewChatWindow} title="New Chat" className="opacity-70 hover:opacity-100 transition-opacity">
            <svg width="28" height="27" viewBox="0 0 28 27" fill="none">
              <rect x="0.5" y="0.5" width="27" height="26" rx="7.5" stroke="url(#cg)" />
              <path d="M8 19L10.5 16.5M18.5 8.5L15 12M10.5 16.5L15 12M10.5 16.5L12 14.5M15 12L12 14.5" stroke="url(#cg)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M16.5 7L20 10.5L17 13.5L13.5 10L16.5 7Z" stroke="url(#cg)" strokeWidth="1" strokeLinejoin="round" />
              <defs><linearGradient id="cg" x1="0" y1="0" x2="28" y2="27" gradientUnits="userSpaceOnUse"><stop stopColor="#004276" /><stop offset="1" stopColor="#004276" /></linearGradient></defs>
            </svg>
          </button>
          {/* Close button — mobile only */}
          <button onClick={() => setMobileSidebarOpen(false)} className="md:hidden p-1 rounded-lg hover:bg-gray-100 text-gray-500">
            <X size={20} />
          </button>
        </div>
      </div>

      {/* History list */}
      <div className="flex-1 overflow-y-auto px-[26px] py-5" style={{ background: "#fff" }}>
        {Object.keys(groupedHistory).length === 0 ? (
          <p className="text-sm text-gray-400 text-center mt-6">No chat history yet</p>
        ) : (
          Object.entries(groupedHistory)
            .sort(([a], [b]) => {
              const rank = (l) =>
                l === "Today" ? 0
                : l === "Yesterday" ? 1
                : l.includes("days back") ? 2
                : l === "This month" ? 3
                : 4; // Older
              return rank(a) - rank(b);
            })
            .map(([group, sessions]) => (
            <div key={group} className="mb-6">
              <p className="text-base font-medium mb-3" style={gradientText}>{group}</p>
              <div className="flex flex-col gap-2">
                {sessions.map((s) => (
  <div
    key={s.session_id}
    className="relative  rounded-[8px] border border-[#e8e8e8] overflow-hidden"
    style={{ background: timeStamp === String(s.session_id) ? "#EAF3FA" : "linear-gradient(282.08deg, #DFF1FB 2.51%, #FFFFFF 100.3%)", boxShadow: timeStamp === String(s.session_id) ? "inset 3px 0 0 0 var(--color-brand-primary)" : "none" }}
  >
    {/* Session button */}
    <button
      onClick={() => { handleChatHistoryClick(s.session_id); setMobileSidebarOpen(false); }}
      className="text-left px-4 py-3 w-full transition-opacity hover:opacity-90 pr-8"
    >
      <span className="text-[14px] font-bold line-clamp-2 block" style={gradientText}>
        {s.session_name || "Unnamed session"}
      </span>
      {s.updated_at && (
        <span className="text-[11px] text-gray-500 mt-0.5 block">
          {new Date(s.updated_at).toLocaleDateString()}
        </span>
      )}
    </button>

    {/* ── DELETE button — appears on hover ── */}
   
    {/* ──────────────────────────────────── */}

  </div>
))}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );

  return (
    <div
     
  className="flex h-[calc(100vh-64px)] overflow-hidden"
      style={{ background: "var(--section-bg-gradient), #fff" }}
    >
      <Toast ref={toastRef} position="top-center" />
      {langModalOpen && <LanguageSelectionModal />}

      {/* ── SIDEBAR — desktop: inline, mobile: slide-over overlay ─────────── */}

      {/* Desktop sidebar (hidden on mobile) */}
      <aside className="hidden md:flex w-[332px] flex-shrink-0 flex-col h-full overflow-hidden">
        {sidebarInner}
      </aside>

      {/* Mobile sidebar backdrop */}
      {mobileSidebarOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/40"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar drawer */}
      <aside
        className={`md:hidden fixed inset-y-0 left-0 z-50 w-[300px] flex flex-col h-full overflow-hidden bg-white shadow-2xl transition-transform duration-300 ${mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        {sidebarInner}
      </aside>

      {/* ── MAIN ─────────────────────────────────────────────────────────── */}
      <main className="flex-1 flex items-stretch justify-center md:px-6 md:py-6 overflow-hidden min-w-0">
        <div
          className="w-full max-w-[1067px] rounded-[12px] md:rounded-[24px] border border-[#dadada] shadow-[0px_0px_30px_0px_rgba(0,0,0,0.25)] flex flex-col overflow-hidden pt-2"
          style={{ background: "var(--section-bg-gradient), #fff" }}
        >
          {/* ── Mobile top bar (history toggle + new chat) ── */}
         <div className="md:hidden flex items-center justify-between px-4 py-2 border-b border-gray-100/80 bg-white/90 backdrop-blur-sm sticky top-0 z-10 flex-shrink-0">
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="flex items-center gap-2 text-sm font-medium"
              style={gradientText}
            >
              <svg width="18" height="14" viewBox="0 0 14 11" fill="none">
                <rect width="14" height="1.8" rx="0.9" fill="url(#mg-m)" />
                <rect y="4.6" width="14" height="1.8" rx="0.9" fill="url(#mg-m)" />
                <rect y="9.2" width="14" height="1.8" rx="0.9" fill="url(#mg-m)" />
                <defs><linearGradient id="mg-m" x1="0" y1="0" x2="14" y2="0" gradientUnits="userSpaceOnUse"><stop stopColor="#004276" /><stop offset="1" stopColor="#004276" /></linearGradient></defs>
              </svg>
              History
            </button>
            <span className="text-sm font-semibold" style={gradientText}>CareSathi</span>
            <button
              onClick={handleNewChatWindow}
              className="text-xs font-medium px-2.5 py-1 rounded-full border"
              style={{ borderColor: "var(--color-brand-primary)", color: "var(--color-brand-primary)" }}
            >
              + New
            </button>
          </div>
           {/* ── Language switcher while chatting ── */}
{!showHeader && (
  <div className="flex justify-end px-4 pt-2 flex-shrink-0">
    <button
      onClick={() => setLangModalOpen(true)}
      className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full border transition-all hover:opacity-80"
      style={{ borderColor: "var(--color-brand-primary)", color: "var(--color-brand-primary)", background: "#EAF3FA" }}
    >
      🌐 {selectedLanguage
        ? (supportedLanguages[selectedLanguage] || selectedLanguage.toUpperCase())
        : "Language"}
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </button>
  </div>
)}
          {/* ── Welcome screen / Chat area ─────────────────────── */}
          {showHeader && qaHistory.length === 0 ? (
            /* Welcome */
            <div className="flex-1 overflow-y-auto flex flex-col items-center justify-center px-4 md:px-8 py-6 md:py-10 gap-4 md:gap-6">
              <div className="text-center">
                <h1 className="text-[26px] md:text-[36px] font-semibold leading-[32px] md:leading-[40px]" style={gradientText}>CareSathi</h1>
                <p className="text-[#004276] text-[16px] md:text-[20px] leading-[28px] md:leading-[40px]">
                  <strong className="font-bold">Care to Cure</strong>
                  <span className="font-normal">, your journey starts here</span>
                </p>
              </div>
              <div className="rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img src={caresathiLogo} alt="CareSathi" className="w-[90px] h-[90px] md:w-[130px] md:h-[130px] object-contain" />
              </div>
              <div className="text-center text-[#3B3B3B] text-[16px] md:text-[20px] leading-[26px] md:leading-[30px]">
                <p className="mb-0">Hello {userName}!</p>
                <p>How Can I help you Today?</p>
              </div>
              <div className="w-full max-w-[814px] px-4 md:px-6 py-[10px] rounded-[8px]" style={{ background: "#fff", border: "1px solid #C9EEF8", boxShadow: "0px 0px 15px 0px #80DAF1" }}>
                  <p className="text-center text-[13px] md:text-[15px] tracking-[0.45px] leading-[22px] md:leading-[25px]" style={gradientText}>
                    CareSathi is now available in <strong className="font-semibold text-[#00BD7F]">English, Hindi, Odia, Bengali, Tamil, Telugu, Marathi, and Kannada</strong> - making healthcare more accessible, inclusive, and closer to every community.
                  </p>

                  {/* Language selector */}
                  <div className="flex items-center justify-center gap-3 mt-3">
                    <span className="text-[13px] font-medium text-[#004276]">Choose your language:</span>
                    <select
                      value={selectedLanguage}
                      onChange={(e) => setSelectedLanguage(e.target.value)}
                      className="text-[13px] border border-[#C9EEF8] rounded-[8px] px-3 py-1.5 outline-none focus:ring-1 focus:ring-[#004276] bg-white"
                      style={{ color: "var(--color-brand-primary)" }}
                    >
                      <option value="">Default (Auto)</option>
                      {Object.entries(supportedLanguages).map(([code, name]) => (
                        <option key={code} value={code}>{name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              <div className="flex flex-col md:flex-row gap-3 md:gap-4 justify-center w-full max-w-[814px]">
                {(SUGGESTIONS[selectedLanguage] || SUGGESTIONS.en).map((s, i) => (
  <button
    key={i}
    onClick={() => { setInput(s); setSampleMessage(s); }}
    className="bg-white border border-[#004276] rounded-[8px] shadow-[0px_4px_10px_0px_#80DAF180] p-3 w-full md:w-[238px] text-left hover:shadow-md transition-shadow"
  >
    <span className="text-[13px] md:text-[14px] font-normal leading-[114%]" style={gradientText}>{s}</span>
  </button>
))}
              </div>
            </div>
          ) : (
            /* Chat messages */
            <div className="flex-1 overflow-y-auto px-3 md:px-6 py-4 space-y-6" ref={chatContainerRef} style={{ scrollbarWidth: "none" }}>
              {qaHistory.map((item, index) => (
                <div key={index} className="flex flex-col gap-3">
                  {/* User bubble */}
                  <div className="flex justify-end">
                    <div className="max-w-[90%] md:max-w-[70%] bg-[#f4f4f4] rounded-[10px] px-4 py-3">
                      <div className="flex items-start gap-2">
                        <img src={caresathiLogo} alt="" className="w-6 h-6 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-[#333]">{item.question}</p>
                      </div>
                    </div>
                  </div>
                  {/* Bot bubble */}
                  <div className="flex justify-start">
                    <div data-answer-index={index} className="max-w-[95%] md:max-w-[85%] rounded-[12px] px-4 py-3 text-white" style={{ background: "var(--primary-gradient-135)" }}>
                      {item.isLoading ? (
                        <div className="flex gap-1 items-center py-1">
                          <div className="w-2 h-2 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                          <div className="w-2 h-2 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                          <div className="w-2 h-2 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-end gap-2 mb-2">
                            {/* Translate */}
                            <div className="relative group">
                              <button className="p-1 rounded hover:bg-white/10 transition-colors">
                                <Languages size={15} className="text-white/80" />
                              </button>
                              <div className="absolute right-0 top-6 bg-white rounded-lg shadow-xl border border-gray-100 z-50 hidden group-hover:block w-36">
                                {getLangItems(index, "answer", item.answer, item.answerLanguage || "en").map((l) => (
                                  <button key={l.code} onClick={l.onClick} className="w-full text-left px-3 py-1.5 text-xs text-[#004276] hover:bg-[#EAF3FA] transition-colors">{l.name}</button>
                                ))}
                              </div>
                            </div>
                            {/* TTS */}
                            {/* TTS */}
<button
  onClick={() => handleSpeak(item.answer, index)}
  disabled={loadingAudioId === index}
  className="p-1 rounded hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
>
  {loadingAudioId === index
    ? <Loader2 size={15} className="text-white/80 animate-spin" />
    : playingAudioId === index
      ? <Pause size={15} className="text-white/80" />
      : <Volume2 size={15} className="text-white/80" />
  }
</button>
                          </div>
                          <div className="text-sm leading-relaxed">
                            <ReactMarkdown
                              components={{
                                p: ({ ...props }) => <p className="mb-2" {...props} />,
                                ul: ({ ...props }) => <ul className="ml-4 mb-2 list-disc" {...props} />,
                                ol: ({ ...props }) => <ol className="ml-4 mb-2 list-decimal" {...props} />,
                                li: ({ ...props }) => <li className="mb-1" {...props} />,
                                strong: ({ ...props }) => <strong className="font-bold" {...props} />,
                                a: ({ ...props }) => <a className="text-blue-200 underline" target="_blank" rel="noopener noreferrer" {...props} />,
                              }}
                            >
                              {convertedText.index === index && convertedText.type === "answer" ? convertedText.text : item.answer}
                            </ReactMarkdown>
                          </div>
                          {/* Service recommendations */}
                          {item?.serviceRecommendation?.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-white/20">
                              <p className="text-xs font-semibold mb-2">Click here to avail service:</p>
                              <div className="flex flex-wrap gap-2">
                                {item.serviceRecommendation.map((rec, idx) =>
                                  rec.link && rec.link.trim() && rec.link.toUpperCase() !== "N/A" ? (
                                    <a key={idx} href={rec.link} target="_blank" rel="noopener noreferrer" className="px-3 py-1 bg-white text-[#004276] rounded-md text-xs font-medium shadow-sm hover:bg-gray-50">{rec.service_name}</a>
                                  ) : (
                                    <button key={idx} onClick={() => sendMessage(rec.service_type, `Please proceed with ${rec.service_name}`)} className="px-3 py-1 bg-white/10 text-white rounded-md text-xs border border-white/30 hover:bg-white/20">{rec.service_name}</button>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                          {/* AI Solution button */}
                      {item?.aiSolution?.url && (
                        <div className="mt-3 pt-3 border-t border-white/20">
                          <p className="text-xs text-white/80 mb-2.5 flex items-center gap-1.5">
                            <span>✨</span>
                            <span>Click below to continue with your personalized {item.aiSolution.display_name} experience</span>
                          </p>
                          <a
                            href={item.aiSolution.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200"
                            style={{
                              background: "linear-gradient(282.08deg, #DFF1FB 2.51%, #FFFFFF 100.3%)",
                              color: "var(--color-brand-primary)",
                              boxShadow: "0 4px 15px rgba(255,255,255,0.25)",
                            }}
                          >
                            <span>{item.aiSolution.display_name || item.aiSolution.solution_key}</span>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                              <polyline points="15 3 21 3 21 9"/>
                              <line x1="10" y1="14" x2="21" y2="3"/>
                            </svg>
                          </a>
                        </div>
                      )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* ── Input area ─────────────────────────────────────────────── */}
          <div className="px-3 md:px-5 pb-4 md:pb-5 pt-3 border-t border-gray-100/80 flex-shrink-0">
            <p className="text-center text-[10px] text-[#584141] font-medium mb-3">
              CureBay AI is a tool for preliminary assessment and not a substitute for professional medical advice.
            </p>

            {chartEnded ? (
              <div className="text-center text-sm py-2">
                Your chat has ended.{" "}
                <button onClick={handleNewChatWindow} className="underline font-medium" style={gradientText}>Start a new conversation</button>
              </div>
            ) : (
              <>
              {uploadingFile && (
  <div className="flex items-center gap-2 mb-2 px-1">
    <Loader2 size={13} className="animate-spin text-[#004276]" />
    <span className="text-xs text-gray-500">Uploading report…</span>
  </div>
)}
                {uploadedFileName && (
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <span className="text-xs text-gray-500"><strong>Uploaded:</strong> {uploadedFileDisplayName || getFileNameFromUrl(uploadedFileName)}</span>
                    <button onClick={() => { setUploadedFileName(""); setUploadedFileDisplayName(""); setBase64pdf(""); setCurrentAttachment(null); }} className="p-0.5 hover:opacity-70">
                      <X size={13} className="text-gray-400" />
                    </button>
                  </div>
                )}
                <div className="bg-white border border-[#d7d7d7] rounded-[36px] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.25)] flex items-center gap-2 md:gap-3 px-3 md:px-5 py-2.5 md:py-3">
                  <div className="flex flex-col items-center cursor-pointer flex-shrink-0" onClick={() => setOpenLabReportModal(true)}>
                    <ArrowUpFromLine size={18} className="text-[#004276]" />
                    <span className="text-[8px] font-bold mt-0.5 leading-tight" style={gradientText}>Upload</span>
                  </div>
                  <input
                    type="text"
                    value={input || sampleMessage}
                    onChange={(e) => { setInput(e.target.value); setSampleMessage(e.target.value); }}
                    onKeyDown={handleKeyDown}
                    placeholder="Please type your health conditions"
                    className="caresathi-chat-input flex-1 text-[14px] text-[#666] placeholder-[#666] bg-transparent border-none outline-none focus:outline-none ring-0 focus:ring-0 shadow-none focus:shadow-none leading-[20px]"
                  />
                  <button
                    onClick={toggleRecording}
                    disabled={micState === "transcribing"}
                    title={
                      micState === "recording" ? "Stop recording"
                      : micState === "transcribing" ? "Transcribing..."
                      : "Record audio"
                    }
                    className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                      micState === "recording"
                        ? "bg-red-500 text-white animate-pulse"
                        : micState === "transcribing"
                          ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                          : "hover:bg-[#EAF3FA] text-[#004276]"
                    }`}
                  >
                    {micState === "recording" ? (
                      <Square size={14} fill="white" />
                    ) : micState === "transcribing" ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Mic size={18} />
                    )}
                  </button>
                  <button onClick={() => sendMessage()} className="flex-shrink-0 w-[39px] h-[39px] rounded-full flex items-center justify-center hover:opacity-90 transition-opacity" style={{ background: "var(--primary-gradient-135)" }}>
                    <Send size={17} className="text-white" strokeWidth={2.5} />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* ── Upload modal ─────────────────────────────────────────────────── */}
      <Dialog header="Share your report" visible={openLabReportModal} onHide={() => setOpenLabReportModal(false)} style={{ width: "400px" }} modal>
        <div className="flex flex-col gap-4 text-center py-2">
          <p className="text-sm text-gray-500">Upload from your device or select from Lab Reports.</p>
          <button onClick={() => fileInputRef.current?.click()} className="w-full py-2.5 rounded-lg text-white text-sm font-medium" style={{ background: "#004171" }}>
            Upload Your Report
          </button>
          <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} />
          <button onClick={() => navigate("/mylabreports?referrer=care-sathi")} className="w-full py-2.5 rounded-lg text-sm font-medium border border-[#004171] text-[#004171]">
            Find Your Lab Report
          </button>
        </div>
      </Dialog>
    </div>
  );
};

export default Newcaresathi;
